import { authenticatedFetch } from "@/api/session";
export type MemberResponse = {
  id: string;
  church_id: string;

  first_name: string;
  middle_name?: string | null;
  last_name: string;
  preferred_name?: string | null;
  photo_url?: string | null;

  email: string | null;
  phone: string | null;

  status: string;
  member_type: string;
  date_joined: string | null;
  last_attended_at: string | null;

  birth_date?: string | null;
  hide_birth_year: boolean;

  anniversary_date?: string | null;
  hide_anniversary_year: boolean;

  household_id: string | null;
  relationship_to_household?: string | null;
  is_head_of_household?: boolean;
};

export type PaginatedMemberResponse = {
  items: MemberResponse[];
  page: number;
  page_size: number;
  total: number;
};

export type MemberPayload = {
  attendance_classification?: "REGULAR" | "YOUTH";
  youth_joined_at?: string | null;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  preferred_name?: string | null;
  photo_url?: string | null;

  email?: string | null;
  phone?: string | null;

  status: string;
  member_type: string;
  date_joined?: string | null;

  birth_date?: string | null;
  hide_birth_year: boolean;

  anniversary_date?: string | null;
  hide_anniversary_year: boolean;

  household_name?: string | null;
  household_id?: string | null;
  relationship_to_household?: string | null;
  is_head_of_household?: boolean;
};

export type MemberRecommendationResponse = {
  priority: string;
  title: string;
  action: string;
  reason: string;
};

export type MemberIntelligenceResponse = {
  score: number;
  level: "HEALTHY" | "WATCH" | "AT_RISK" | "CRITICAL";
  confidence: number;
  generated_at: string;
  engine_version: string;
  reasons: string[];
  recommendations: MemberRecommendationResponse[];
};

export type MemberAttendanceRecord = {
  id: string;
  service_date: string;
  service_type: string;
  status: string;
  attendance_source: "MEMBER" | "VISITOR";
  service_occurrence_id?: string | null;
  ministry_id?: string | null;
  ministry_name?: string;
  ministry_archived?: boolean;
};

export type MemberAttendanceSummary = {
  eligible_services: number;
  attended_services: number;
  missed_services: number;
  attendance_rate: number | null;
  consecutive_absences: number;
};

export type HouseholdMember = {
  id: string;
  first_name: string;
  last_name: string;
  relationship_to_household?: string | null;
  is_head_of_household: boolean;
};

export type HouseholdSummary = {
  id: string;
  household_name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  phone?: string | null;
  email?: string | null;
  health_score: number | null;
  attendance_rate: number | null;
  open_care_items: number | null;
  member_count: number;
  members: HouseholdMember[];
};

export type MemberActivity = {
  id: string;
  type: string;
  title: string;
  description?: string | null;
  occurred_at: string;
};

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  "http://localhost:8000";

function getAuthHeaders() {
  const token = localStorage.getItem("access_token");

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function parseResponse<T>(
  response: Response,
  errorMessage: string
): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail =
      body && typeof body.detail === "string" ? body.detail : errorMessage;

    throw new Error(detail);
  }

  return response.json() as Promise<T>;
}

export async function getMembers(
  churchId: string,
  page = 1,
  pageSize = 25,
  search = ""
): Promise<PaginatedMemberResponse> {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (search) {
    params.set("search", search);
  }

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members?${params.toString()}`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load members");
}

export async function getMember(
  churchId: string,
  memberId: string
): Promise<MemberResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load member");
}

export async function createMember(
  churchId: string,
  payload: MemberPayload
): Promise<MemberResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...payload,
        church_id: churchId,
      }),
    }
  );

  return parseResponse(response, "Failed to create member");
}

export async function updateMember(
  churchId: string,
  memberId: string,
  payload: Partial<MemberPayload>
): Promise<MemberResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );

  return parseResponse(response, "Failed to update member");
}

export async function getMemberIntelligence(
  churchId: string,
  memberId: string
): Promise<MemberIntelligenceResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}/intelligence`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load member intelligence");
}

export async function getMemberAttendanceHistory(
  churchId: string,
  memberId: string,
  month?: string
): Promise<MemberAttendanceRecord[]> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}/attendance-history${month ? `?month=${encodeURIComponent(month)}` : ""}`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load attendance history");
}

export async function getMemberAttendanceSummary(
  churchId: string,
  memberId: string,
  limit = 12
): Promise<MemberAttendanceSummary> {
  const params = new URLSearchParams({
    limit: String(limit),
  });

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}/attendance-summary?${params.toString()}`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(
    response,
    "Failed to load member attendance summary"
  );
}

export async function getHouseholdSummary(
  churchId: string,
  memberId: string
): Promise<HouseholdSummary | null> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}/household-summary`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load household");
}

export async function getMemberActivityTimeline(
  churchId: string,
  memberId: string
): Promise<MemberActivity[]> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}/activity-timeline`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load activity timeline");
}

export type MemberDeletionStatus = {
  can_delete: boolean;
  reasons: string[];
  household_notice: string | null;
};

export async function getMemberDeletionStatus(churchId: string, memberId: string): Promise<MemberDeletionStatus> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}/deletion-status`,
    { headers: getAuthHeaders() },
  );
  return parseResponse(response, "Unable to check member deletion safety");
}

export async function deleteMember(churchId: string, memberId: string): Promise<void> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}`,
    { method: "DELETE", headers: getAuthHeaders() },
  );
  if (!response.ok) await parseResponse(response, "Unable to delete member");
}


export type MemberAssessmentContext = {
  context_name: string;
  ministry_id: string | null;
  assessment_status: "ASSESSED" | "NOT_ASSESSED";
  assessment: MemberIntelligenceResponse | null;
  attendance?: { eligible_occurrences?: number; attended_occurrences?: number; attendance_rate?: number | null; consecutive_absences: number } | null;
};
export async function getMemberAssessmentContext(churchId: string, memberId: string): Promise<MemberAssessmentContext> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/members/${memberId}/intelligence/attendance-context`,
    { headers: getAuthHeaders() },
  );
  return parseResponse(response, "Failed to load member assessment context");
}
